import * as React from 'react'

import { RateConfigurationDetailPageDocument } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { NavigationRoutesFn } from 'modules/core/lib/react-router'

import { getTranslatedPageTitle } from 'modules/expenses/pages/settings/FormFieldsPage'
import { ExpensableRateConfigurationResourceTypeEnum } from 'modules/expenses/types/Expensable'

import { RateHeader } from './RateHeader'

const RootPage = React.lazy(() => import('modules/expenses/pages/settings/RootPage'))
const ExpensesV2Settings = React.lazy(() => import('modules/expenses/pages/settings'))
const FormFieldEditModalPage = React.lazy(
  () => import('modules/expenses/pages/settings/FormFieldEditModalPage')
)
const FormFieldsPage = React.lazy(() => import('modules/expenses/pages/settings/FormFieldsPage'))
const FormsCustomizationRootPage = React.lazy(
  () => import('modules/expenses/pages/settings/FormsCustomizationRootPage')
)
const RateConfigurationsPage = React.lazy(
  () => import('modules/expenses/pages/settings/RateConfigurationsPage')
)

const RateConfigurationsListPage = React.lazy(
  () => import('modules/expenses/pages/settings/RateConfigurationsPage/List')
)

const CompanyPerDiemRatesList = React.lazy(
  () => import('modules/expenses/pages/settings/CompanyPerDiemRatesList')
)

const RateConfigurationsDetailPage = React.lazy(
  () => import('modules/expenses/pages/settings/RateConfigurationsPage/Detail')
)

const SignedInvoiceDetailPage = React.lazy(
  () => import('modules/expenses/pages/SignedInvoiceDetailPage')
)
const SignedInvoiceExportPage = React.lazy(
  () => import('modules/expenses/pages/SignedInvoiceExportPage')
)
const SignedInvoicesTablePage = React.lazy(
  () => import('modules/expenses/pages/SignedInvoicesTablePage')
)
const ItalyLegalEntitiesPage = React.lazy(
  () => import('modules/expenses/pages/settings/Content/ItalyHomologation/ItalyLegalEntitiesPage')
)

const settingsRoutes: NavigationRoutesFn = (navigation) => {
  return [
    {
      path: navigation.settings.expenses.path,
      element: <RootPage />,
      handle: {
        meta: () => ({
          title: i18n.t('app_store_v2.apps.expenses.title'),
        }),
      },
      children: [
        {
          element: <ExpensesV2Settings />,
          index: true,
        },
        {
          path: navigation.settings.expenses.expensesForms.path,
          element: <FormsCustomizationRootPage />,
          handle: {
            meta: ({ params }) => {
              return {
                title: params['resource_type']
                  ? getTranslatedPageTitle(params['resource_type'])
                  : i18n.t('expenses_v2.settings.forms.title'),
              }
            },
          },
          children: [
            {
              path: navigation.settings.expenses.expensesForms.fields.path,
              element: <FormFieldsPage />,
              children: [
                {
                  path: navigation.settings.expenses.expensesForms.fields.edit.path,
                  element: <FormFieldEditModalPage />,
                },
                {
                  path: navigation.settings.expenses.expensesForms.fields.editByIdentifier.path,
                  element: <FormFieldEditModalPage />,
                },
              ],
            },
          ],
        },
        {
          path: navigation.settings.expenses.rateConfigurations.path,
          handle: {
            meta: () => ({
              title: i18n.t('expenses_v2.settings.rate_configurations.per_diem_title'),
              description: i18n.t('expenses_v2.settings.rate_configurations.per_diem_description'),
            }),
          },
          element: <RateConfigurationsPage />,
          children: [
            {
              index: true,
              element: (
                <RateConfigurationsListPage
                  resourceType={ExpensableRateConfigurationResourceTypeEnum.PerDiem}
                />
              ),
            },
            {
              path: navigation.settings.expenses.rateConfigurations.rateId.path,
              handle: {
                resource: true,
                meta: ({ watchQuery, params, session }) => {
                  return watchQuery({
                    query: RateConfigurationDetailPageDocument,
                    variables: {
                      id: Number(params.rateId),
                      companyId: session.company.id,
                    },
                  }).map(({ data }) => {
                    const rateConfiguration = data?.expenses.rateConfigurationsConnection.nodes[0]
                    return {
                      title: rateConfiguration?.name,

                      headerComponent: (
                        <RateHeader
                          rate={rateConfiguration}
                          type={ExpensableRateConfigurationResourceTypeEnum.PerDiem}
                        />
                      ),
                    }
                  })
                },
              },
              element: (
                <RateConfigurationsDetailPage
                  resourceType={ExpensableRateConfigurationResourceTypeEnum.PerDiem}
                />
              ),
            },
          ],
        },
        {
          path: navigation.settings.expenses.mileageRates.path,
          handle: {
            meta: () => ({
              title: i18n.t('expenses_v2.settings.rate_configurations.mileage_title'),
              description: i18n.t('expenses_v2.settings.rate_configurations.mileage_description'),
            }),
          },
          element: <RateConfigurationsPage />,
          children: [
            {
              index: true,
              element: (
                <RateConfigurationsListPage
                  resourceType={ExpensableRateConfigurationResourceTypeEnum.Mileage}
                />
              ),
            },
            {
              path: navigation.settings.expenses.mileageRates.rateId.path,
              handle: {
                resource: true,
                meta: ({ watchQuery, params, session }) => {
                  return watchQuery({
                    query: RateConfigurationDetailPageDocument,
                    variables: {
                      id: Number(params.rateId),
                      companyId: session.company.id,
                    },
                  }).map(({ data }) => {
                    const rateConfiguration = data?.expenses.rateConfigurationsConnection.nodes[0]
                    return {
                      title: rateConfiguration?.name,

                      headerComponent: (
                        <RateHeader
                          rate={rateConfiguration}
                          type={ExpensableRateConfigurationResourceTypeEnum.Mileage}
                        />
                      ),
                    }
                  })
                },
              },
              element: (
                <RateConfigurationsDetailPage
                  resourceType={ExpensableRateConfigurationResourceTypeEnum.Mileage}
                />
              ),
            },
          ],
        },
        {
          path: navigation.settings.expenses.perdiemRates.path,
          handle: {
            meta: () => ({
              title: i18n.t('expenses_v2.settings.rate_configurations.germany_per_diem_title'),
              description: i18n.t(
                'expenses_v2.settings.rate_configurations.germany_per_diem_description'
              ),
            }),
          },
          element: <RateConfigurationsPage />,
          children: [
            {
              index: true,
              element: <CompanyPerDiemRatesList />,
            },
          ],
        },
        {
          path: navigation.settings.expenses.signedInvoices.path,
          handle: {
            meta: () => ({
              title: i18n.t('expenses.settings.signed_invoices.list.title'),
            }),
          },
          children: [
            {
              path: navigation.settings.expenses.signedInvoices.invoiceId.path,
              element: <SignedInvoiceDetailPage />,
            },
            {
              path: navigation.settings.expenses.signedInvoices.exports.path,
              element: <SignedInvoiceExportPage />,
            },
          ],
          element: <SignedInvoicesTablePage />,
        },
        {
          path: navigation.settings.expenses.italyHomologation.path,
          handle: {
            meta: () => ({
              title: i18n.t('expenses.settings.signed_invoices.italy.legal_entities.title'),
            }),
          },
          element: <ItalyLegalEntitiesPage />,
        },
      ],
    },
  ]
}

export default settingsRoutes
